import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import { Toaster } from 'react-hot-toast';
import { usePushNotifications } from './hooks/usePushNotifications';

import UserManagement from './pages/RepPortal/UserManagement';

import Login from './pages/Login';
import ClientLogin from './pages/ClientLogin';
import ResetPassword from './pages/ResetPassword';
import Register from './pages/Register';
import Navbar from './components/Navbar';
import Sidebar from './components/Sidebar';
import BottomNav from './components/BottomNav';

// Rep / Admin pages
import RepDashboard from './pages/RepPortal/Dashboard';
import RepInfo from './pages/RepPortal/RepInfo';
import ClientsPage from './pages/RepPortal/Clients';
import ClientDetail from './pages/RepPortal/ClientDetail';
import SecureUpload from './pages/RepPortal/SecureUpload';
import DocumentCenter from './pages/RepPortal/DocumentCenter';
const AutoUnderwriting = lazy(() => import('./pages/RepPortal/Underwriting/AutoUnderwriting'));
import DealLog from './pages/RepPortal/DealLog';
const CommissionCalculator = lazy(() => import('./pages/RepPortal/CommissionCalculator'));
import HelpPage from './pages/RepPortal/Help';
import RequestsPage from './pages/RepPortal/Requests';
import NotesPage from './pages/RepPortal/Notes';
import ActivityPage from './pages/RepPortal/Activity';
import Training from './pages/RepPortal/Training';
const CILoc = lazy(() => import('./pages/RepPortal/CILoc'));
import ClientData from './pages/RepPortal/ClientData';
import RepData from './pages/RepPortal/RepData';
import LeadData from './pages/RepPortal/LeadData';
import Portfolio from './pages/RepPortal/Portfolio';
import Messages from './pages/RepPortal/Messages';
import Messaging from './pages/RepPortal/Messaging';
const NexusBot = lazy(() => import('./pages/RepPortal/NexusBot'));
const RocketReach = lazy(() => import('./pages/RepPortal/RocketReach'));
const FullClientData = lazy(() => import('./pages/RepPortal/FullClientData'));
const FundingBook = lazy(() => import('./pages/RepPortal/FundingBook'));
const LeadCorroborator = lazy(() => import('./pages/RepPortal/LeadCorroborator'));
const PullerData = lazy(() => import('./pages/RepPortal/PullerData'));
const AutoSubmit = lazy(() => import('./pages/RepPortal/AutoSubmit'));
const Submissions = lazy(() => import('./pages/RepPortal/Submissions'));
const AdminRequests = lazy(() => import('./pages/RepPortal/AdminRequests'));
const LenderMetrics = lazy(() => import('./pages/RepPortal/LenderMetrics'));
const SubmissionMetrics = lazy(() => import('./pages/RepPortal/SubmissionMetrics'));

// Client pages
import ClientDashboard from './pages/ClientPortal/Dashboard';
import MyDocuments from './pages/ClientPortal/MyDocuments';
import UploadCenter from './pages/ClientPortal/UploadCenter';
import ClientRequests from './pages/ClientPortal/Requests';
import ClientStatus from './pages/ClientPortal/Status';
import ClientProfile from './pages/ClientPortal/Profile';
import MyBusinesses from './pages/ClientPortal/MyBusinesses';
import ClientLoc from './pages/ClientPortal/ClientLoc';
import ClientCredentials from './pages/RepPortal/ClientCredentials';
import ClientLogins from './pages/RepPortal/ClientLogins';
import SecureUploadLink from './pages/SecureUploadLink';

function AppShell() {
  const { user, viewMode } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Register for push notifications on native (iOS/Android) — no-op in browser
  usePushNotifications(user?.id);

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<ClientLogin />} />
        <Route path="/admin-login" element={<Login />} />
        <Route path="/client-login" element={<ClientLogin />} />
        <Route path="/register" element={<Register />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/secure-upload/:token" element={<SecureUploadLink />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: 'var(--surface-sunken)' }}>
      <Sidebar mobileOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />
        <main id="orbit-main" className="flex-1 overflow-y-auto pb-16 md:pb-0 px-3 md:px-0" style={{ background: 'var(--surface-sunken)' }}>
          <Suspense fallback={<div className="p-6 text-gray-400 text-sm">Loading...</div>}>
            <div className="animate-fadeIn">
              {viewMode === 'client' ? <ClientRoutes /> : <RepRoutes />}
            </div>
          </Suspense>
        </main>
      </div>
      <BottomNav onMenuTap={() => setSidebarOpen(true)} />
    </div>
  );
}

function RepRoutes() {
  return (
    <Routes>
      <Route path="/" element={<RepDashboard />} />
      <Route path="/reps" element={<RepInfo />} />
      <Route path="/clients" element={<ClientsPage />} />
      <Route path="/clients/:id" element={<ClientDetail />} />
      <Route path="/upload" element={<SecureUpload />} />
      <Route path="/documents" element={<DocumentCenter />} />
      <Route path="/underwriting" element={<AutoUnderwriting />} />
      <Route path="/deals" element={<DealLog />} />
      <Route path="/commissions" element={<CommissionCalculator />} />
      <Route path="/help" element={<HelpPage />} />
      <Route path="/requests" element={<RequestsPage />} />
      <Route path="/notes" element={<NotesPage />} />
      <Route path="/activity" element={<ActivityPage />} />
      <Route path="/training" element={<Training />} />
      <Route path="/ci-loc" element={<CILoc />} />
      <Route path="/client-data" element={<ClientData />} />
      <Route path="/rep-data" element={<RepData />} />
      <Route path="/waymo-data" element={<LeadData />} />
      <Route path="/full-client-data" element={<FullClientData />} />
      <Route path="/portfolio" element={<Portfolio />} />
      <Route path="/messages" element={<Messages />} />
      <Route path="/messaging" element={<Messaging />} />
      <Route path="/nexus-bot" element={<NexusBot />} />
      <Route path="/funding-book" element={<FundingBook />} />
      <Route path="/lead-corroborator" element={<LeadCorroborator />} />
      <Route path="/puller-data" element={<PullerData />} />
      <Route path="/auto-submit" element={<AutoSubmit />} />
      <Route path="/submissions" element={<Submissions />} />
      <Route path="/admin" element={<AdminRequests />} />
      <Route path="/lender-metrics" element={<LenderMetrics />} />
      <Route path="/submission-metrics" element={<SubmissionMetrics />} />
      <Route path="/email-eagle" element={<RocketReach />} />
      <Route path="/my-orbit" element={<MyOrbitPage />} />
      <Route path="/users" element={<UserManagement />} />
      <Route path="/client-credentials" element={<ClientCredentials />} />
      <Route path="/client-logins" element={<ClientLogins />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function ClientRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/upload" replace />} />
      <Route path="/my-documents" element={<MyDocuments />} />
      <Route path="/upload" element={<UploadCenter />} />
      <Route path="/requests" element={<ClientRequests />} />
      <Route path="/status" element={<ClientStatus />} />
      <Route path="/profile" element={<ClientProfile />} />
      <Route path="/businesses" element={<MyBusinesses />} />
      <Route path="/my-loc" element={<ClientLoc />} />
      <Route path="*" element={<Navigate to="/upload" replace />} />
    </Routes>
  );
}

function MyOrbitPage() {
  const { user, token } = useAuth();
  const API = process.env.REACT_APP_API_URL || 'https://api.orbit-technology.com/api';
  const [profile, setProfile] = useState(null);
  const [pic, setPic] = useState(localStorage.getItem('orbit_pic') || '');

  useEffect(() => {
    if (!token) return;
    fetch(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setProfile(d); })
      .catch(() => {});
  }, [token]);

  const headshot = profile?.headshot || pic;
  const handlePic = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => { const url = ev.target.result; setPic(url); localStorage.setItem('orbit_pic', url); };
    reader.readAsDataURL(file);
  };

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-5">
      <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">My Orbit</h1>
      <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-6 flex items-center gap-6">
        <div className="relative">
          <div className="w-20 h-20 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-600 font-bold text-2xl overflow-hidden">
            {headshot ? <img src={headshot} alt="" className="w-full h-full object-cover" /> : (user?.name?.[0] || '?')}
          </div>
          <label className="absolute -bottom-1 -right-1 w-7 h-7 bg-blue-600 rounded-full flex items-center justify-center cursor-pointer hover:bg-blue-700 transition-colors">
            <span className="text-white text-xs">✎</span>
            <input type="file" accept="image/*" className="hidden" onChange={handlePic} />
          </label>
        </div>
        <div>
          <p className="text-gray-900 font-semibold text-lg">{user?.name}</p>
          <p className="text-gray-400 text-sm">{user?.email}</p>
          <p className="text-gray-500 text-xs mt-1 capitalize">{user?.email === 'matthew@capital-infusion.com' ? 'CEO' : user?.role} · Active</p>
          {profile?.phone && <p className="text-gray-400 text-xs mt-0.5">{profile.phone}</p>}
        </div>
      </div>

      {/* THE PER-REP UPLOAD LINK IS NO LONGER OFFERED.
          Document acquisition goes through the Request feature on the client's
          own page, which sends the general link and lands the file on the right
          client record, already assigned.

          A personal link did neither: it created its own client record from
          whatever the merchant typed, so a deal that already existed got a
          second record and the file landed on that one instead of the real
          deal. It was used once in the whole history of the store - GRP
          Contracting, 25 September - and the confusion it caused the one time
          it was reached for outweighed the convenience.

          The /secure-upload/:token route and its two endpoints are left in
          place deliberately: links already sent to merchants still work, so
          nobody is left with a dead URL. This only stops new ones being handed
          out. */}
    </div>
  );
}

export default function App() {
  // v8-cache-bust
  return (
    <LanguageProvider>
    <AuthProvider>
        <Router>
          <Toaster
            position="top-right"
            toastOptions={{
              style: { background: '#fff', color: '#1d1d1f', border: '1px solid #e5e5ea', borderRadius: '12px', fontSize: '13px' },
            }}
          />
          <AppShell />
        </Router>
      </AuthProvider>
    </LanguageProvider>
  );
}
